// PROMPT 151: difusiones de WhatsApp: una plantilla aprobada a muchos clientes, con vista previa, limites y
// seguimiento. Solo para administradores (el servidor lo vuelve a comprobar en cada pedido).
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Download, Loader2, Megaphone, Pause, Play, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AUTOCOMPLETAR, categoriaNombre } from "@/lib/plantillasWa";
import { nombreCorto } from "@/lib/marcasWa";

const BASE = "/whatsapp/difusiones";
const REFRESCO_MS = 5000;
const NOTA_COBRO = "Cada mensaje entregado lo cobra Meta según la categoría y el país.";
const PASOS = ["Marca y nombre", "Plantilla", "Destinatarios", "Vista previa", "Confirmar"];
const ESTADOS_DIFUSION = {
  borrador: ["Borrador", "bg-slate-100 text-slate-700"],
  lista: ["Lista para mandar", "bg-sky-100 text-sky-800"],
  enviando: ["Enviando", "bg-emerald-100 text-emerald-800"],
  pausada: ["Pausada", "bg-amber-100 text-amber-900"],
  terminada: ["Terminada", "bg-slate-200 text-slate-800"],
  cancelada: ["Cancelada", "bg-red-100 text-red-800"],
};
const ESTADOS_ENVIO = {
  pendiente: "Pendiente",
  enviando: "Enviando",
  enviado: "Enviado",
  entregado: "Entregado",
  leido: "Leído",
  fallido: "Falló",
  dudoso: "Dudoso",
  omitido: "Omitido",
  cancelado: "Cancelado",
};
const CONTADORES = [
  ["pendientes", "Pendientes"],
  ["enviados", "Enviados"],
  ["entregados", "Entregados"],
  ["leidos", "Leídos"],
  ["fallidos", "Fallidos"],
  ["dudosos", "Dudosos"],
  ["omitidos", "Omitidos"],
  ["cancelados", "Cancelados"],
];
const TIPOS = [
  ["reservas", "Reservas por fecha de tour"],
  ["conversaciones", "Conversaciones de la marca"],
  ["lista", "Lista de teléfonos"],
];
// Fix 155 (2026-10-22): las archivadas ya no son un estado mas: se piden con "Incluir las conversaciones archivadas"
// (contrato 151, `incluir_archivadas`).
const ESTADOS_CONVERSACION = [
  ["agente", "La atiende el agente"],
  ["persona", "La atiende una persona"],
];
// Fix 155 (2026-10-22): estados de reserva a elegir (contrato 151): por defecto solo las confirmadas.
const ESTADOS_DE_RESERVA = [
  ["confirmada", "Confirmadas"],
  ["pendiente", "Pendientes"],
  ["reprogramada", "Reprogramadas"],
];
const FORM_VACIO = {
  marca: "",
  nombre: "",
  plantillaId: "",
  valores: {},
  tipo: "reservas",
  desde: "",
  hasta: "",
  estados: [],
  // Fix 155 (2026-10-22): reservas por estado (por defecto las confirmadas) y por tour; conversaciones archivadas.
  estadosReserva: ["confirmada"],
  tourIds: [],
  incluirArchivadas: false,
  etiquetas: [],
  texto: "",
  acepto: false,
};

// Los valores de la plantilla viajan como el servidor los guarda: { cuerpo: { clave: valor }, botones: {...} }.
const grupoServidor = (c) => (c.grupo === "boton" ? "botones" : c.grupo);

function valorDe(valores, c) {
  const g = (valores || {})[grupoServidor(c)];
  const v = g ? g[c.clave] : "";
  return v === undefined || v === null ? "" : String(v);
}

function conValor(valores, c, texto) {
  const g = grupoServidor(c);
  return { ...(valores || {}), [g]: { ...((valores || {})[g] || {}), [c.clave]: texto } };
}

function valoresLimpios(valores, campos) {
  const salida = {};
  (campos || []).forEach((c) => {
    const v = valorDe(valores, c).trim();
    if (v) salida[grupoServidor(c)] = { ...(salida[grupoServidor(c)] || {}), [c.clave]: v };
  });
  return salida;
}

function origenDe(f) {
  if (f.tipo === "reservas") {
    return {
      tipo: "reservas",
      fecha_desde: f.desde || null,
      fecha_hasta: f.hasta || null,
      tour_ids: f.tourIds,
      estados: f.estadosReserva,
    };
  }
  if (f.tipo === "conversaciones") {
    return { tipo: "conversaciones", estados: f.estados, etiquetas: f.etiquetas, incluir_archivadas: f.incluirArchivadas };
  }
  return { tipo: "lista", texto: f.texto, acepto_mensajes: f.acepto };
}

function formDe(d) {
  const o = d.origen || {};
  return {
    ...FORM_VACIO,
    marca: d.marca_slug || "",
    nombre: d.nombre || "",
    plantillaId: d.plantilla_id || "",
    valores: d.valores || {},
    tipo: o.tipo || "reservas",
    desde: o.fecha_desde || "",
    hasta: o.fecha_hasta || "",
    estados: o.tipo === "conversaciones" ? o.estados || [] : [],
    estadosReserva: o.tipo === "reservas" && (o.estados || []).length > 0 ? o.estados : ["confirmada"],
    tourIds: o.tour_ids || (o.tour_id ? [o.tour_id] : []),
    incluirArchivadas: Boolean(o.incluir_archivadas),
    etiquetas: o.etiquetas || [],
    texto: o.texto || "",
    acepto: Boolean(o.acepto_mensajes),
  };
}

function fechaHora(valor) {
  try {
    const f = new Date(valor);
    if (Number.isNaN(f.getTime())) return "";
    return f.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function EstadoChip({ estado }) {
  const par = ESTADOS_DIFUSION[estado] || [estado || "", "bg-slate-100 text-slate-700"];
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${par[1]}`}
      data-testid="wa-dif-estado"
    >
      {par[0]}
    </span>
  );
}

function Aviso({ tono, testid, children }) {
  const clases =
    tono === "rojo"
      ? "border-red-200 bg-red-50 text-red-800"
      : tono === "ambar"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div
      className={`rounded-md border px-3 py-2 text-sm ${clases}`}
      role={tono === "rojo" ? "alert" : undefined}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function Leyendo({ texto }) {
  return (
    <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> {texto}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ la lista
function Lista({ marcas, onNueva, onAbrir }) {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let vivo = true;
    api
      .get(BASE)
      .then(({ data }) => {
        if (vivo) setFilas((data && data.difusiones) || []);
      })
      .catch((e) => {
        if (vivo) {
          setFilas([]);
          setError(formatApiError(e.response?.data?.detail));
        }
      });
    return () => {
      vivo = false;
    };
  }, []);

  return (
    <div className="space-y-3" data-testid="wa-dif-lista">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">Armá una difusión y seguila desde acá.</p>
        <Button size="sm" onClick={onNueva} className="gap-1.5" data-testid="wa-dif-nueva">
          <Plus className="h-4 w-4" />
          Nueva difusión
        </Button>
      </div>
      {error ? (
        <Aviso tono="rojo" testid="wa-dif-error">
          {error}
        </Aviso>
      ) : null}
      {filas === null ? <Leyendo texto="Leyendo las difusiones…" /> : null}
      {filas && filas.length === 0 ? (
        <div
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500"
          data-testid="wa-dif-vacia"
        >
          <Megaphone className="h-6 w-6 text-slate-400" />
          Todavía no hay difusiones.
        </div>
      ) : null}
      {filas && filas.length > 0 ? (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {filas.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => onAbrir(f.id)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
                data-testid={`wa-dif-fila-${f.id}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">{f.nombre}</span>
                  <span className="block truncate text-[11px] text-slate-500">
                    {nombreCorto(f.marca_slug, marcas)} · {f.plantilla_nombre} · {fechaHora(f.created_at)}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-slate-500">{f.destinatarios || 0} clientes</span>
                <EstadoChip estado={f.estado} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------------------------- los pasos del asistente
function PasoMarca({ form, poner, marcas }) {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="wa-dif-nombre" className="text-xs text-slate-600">
          Nombre de la difusión (lo ve solo el equipo)
        </Label>
        <Input
          id="wa-dif-nombre"
          value={form.nombre}
          maxLength={120}
          onChange={(e) => poner({ nombre: e.target.value })}
          placeholder="Promo de otoño"
          className="mt-1 text-base lg:text-sm"
          data-testid="wa-dif-nombre"
        />
      </div>
      <div>
        <Label htmlFor="wa-dif-marca" className="text-xs text-slate-600">
          Marca (sale por el número de esta marca)
        </Label>
        <select
          id="wa-dif-marca"
          value={form.marca}
          onChange={(e) => poner({ marca: e.target.value, plantillaId: "", valores: {} })}
          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 lg:text-sm"
          data-testid="wa-dif-marca"
        >
          <option value="">Elegí una marca</option>
          {(marcas || []).map((m) => (
            <option key={m.slug} value={m.slug}>
              {nombreCorto(m.slug, marcas)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function PasoPlantilla({ form, poner, plantillas, errorPlantillas, elegida, campos }) {
  if (plantillas === null) return <Leyendo texto="Leyendo las plantillas de la marca…" />;
  const lista = plantillas.filter((p) => String(p.categoria || "").toUpperCase() !== "AUTHENTICATION");
  return (
    <div className="space-y-3">
      {errorPlantillas ? (
        <Aviso tono="rojo" testid="wa-dif-error-plantillas">
          {errorPlantillas}
        </Aviso>
      ) : null}
      {lista.length === 0 ? (
        <Aviso tono="ambar" testid="wa-dif-sin-plantillas">
          Esta marca no tiene plantillas aprobadas. Se crean y se mandan a Meta desde Plantillas.
        </Aviso>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {lista.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => poner({ plantillaId: p.id, valores: {} })}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                  p.id === form.plantillaId ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`wa-dif-plantilla-${p.id}`}
              >
                <span className="block truncate font-medium text-slate-800">{p.nombre}</span>
                <span className="block text-[11px] text-slate-500">
                  {categoriaNombre(p.categoria)} · {p.idioma}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {elegida ? (
        <div className="space-y-2 rounded-md bg-slate-50 p-3" data-testid="wa-dif-variables">
          {campos.map((c) => {
            const auto = AUTOCOMPLETAR.find((a) => a.valor === c.autocompletar);
            const id = `wa-dif-campo-${c.grupo}-${c.clave}`;
            return (
              <div key={id} className="space-y-1">
                <Label htmlFor={id} className="text-xs text-slate-600">
                  {c.etiqueta}
                  {c.grupo === "encabezado" ? " (encabezado)" : ""}
                </Label>
                <Input
                  id={id}
                  value={valorDe(form.valores, c)}
                  onChange={(e) => poner({ valores: conValor(form.valores, c, e.target.value) })}
                  placeholder={
                    c.autocompletar
                      ? `Vacío: se completa con ${auto ? auto.nombre.toLowerCase() : c.autocompletar} de cada cliente`
                      : c.ejemplo || ""
                  }
                  className="bg-white text-base lg:text-sm"
                  data-testid={id}
                />
              </div>
            );
          })}
          {campos.length === 0 ? (
            <p className="text-sm text-slate-500">Esta plantilla no tiene variables para completar.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PasoDestinatarios({ form, poner, etiquetas, tours = [] }) {
  const alternar = (campo, valor) => {
    const hoy = form[campo];
    poner({ [campo]: hoy.includes(valor) ? hoy.filter((x) => x !== valor) : [...hoy, valor] });
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="De dónde salen los destinatarios">
        {TIPOS.map(([valor, nombre]) => (
          <button
            key={valor}
            type="button"
            aria-pressed={form.tipo === valor}
            onClick={() => poner({ tipo: valor })}
            className={`rounded-full border px-3 py-1 text-sm ${
              form.tipo === valor ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700"
            }`}
            data-testid={`wa-dif-tipo-${valor}`}
          >
            {nombre}
          </button>
        ))}
      </div>
      {form.tipo === "reservas" ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="wa-dif-desde" className="text-xs text-slate-600">
                Tour desde
              </Label>
              <Input
                id="wa-dif-desde"
                type="date"
                value={form.desde}
                onChange={(e) => poner({ desde: e.target.value })}
                className="mt-1 text-base lg:text-sm"
                data-testid="wa-dif-desde"
              />
            </div>
            <div>
              <Label htmlFor="wa-dif-hasta" className="text-xs text-slate-600">
                Tour hasta
              </Label>
              <Input
                id="wa-dif-hasta"
                type="date"
                value={form.hasta}
                onChange={(e) => poner({ hasta: e.target.value })}
                className="mt-1 text-base lg:text-sm"
                data-testid="wa-dif-hasta"
              />
            </div>
          </div>
          {/* Fix 155 (2026-10-22): contrato 151: elegir el estado de la reserva (por defecto, las confirmadas) y los tours. */}
          <p className="text-xs text-slate-600">Estado de la reserva</p>
          <div className="flex flex-wrap gap-3">
            {ESTADOS_DE_RESERVA.map(([valor, nombre]) => (
              <label key={valor} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-emerald-600"
                  checked={form.estadosReserva.includes(valor)}
                  onChange={() => alternar("estadosReserva", valor)}
                  data-testid={`wa-dif-estado-reserva-${valor}`}
                />
                {nombre}
              </label>
            ))}
          </div>
          {tours.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs text-slate-600">Tours (sin elegir, entran todos)</p>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                {tours.map((t) => (
                  <label key={t.id} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-emerald-600"
                      checked={form.tourIds.includes(t.id)}
                      onChange={() => alternar("tourIds", t.id)}
                      data-testid={`wa-dif-tour-${t.id}`}
                    />
                    {t.nombre}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <p className="text-[11px] text-slate-500">
            Entran solo los clientes que ya escribieron por WhatsApp a esta marca.
          </p>
        </div>
      ) : null}
      {form.tipo === "conversaciones" ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-600">Quién la atiende (sin elegir, entran las dos)</p>
          <div className="flex flex-wrap gap-3">
            {ESTADOS_CONVERSACION.map(([valor, nombre]) => (
              <label key={valor} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-emerald-600"
                  checked={form.estados.includes(valor)}
                  onChange={() => alternar("estados", valor)}
                  data-testid={`wa-dif-estado-conv-${valor}`}
                />
                {nombre}
              </label>
            ))}
          </div>
          {/* Fix 155 (2026-10-22): contrato 151 ("incluir_archivadas"): las archivadas no entran salvo que se pida. */}
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 accent-emerald-600"
              checked={form.incluirArchivadas}
              onChange={(e) => poner({ incluirArchivadas: e.target.checked })}
              data-testid="wa-dif-archivadas"
            />
            Incluir las conversaciones archivadas
          </label>
          {etiquetas.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs text-slate-600">Etiquetas (entran las que tengan alguna de las elegidas)</p>
              <div className="flex flex-wrap gap-1.5">
                {etiquetas.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    aria-pressed={form.etiquetas.includes(e.id)}
                    onClick={() => alternar("etiquetas", e.id)}
                    className={`rounded-full border px-2.5 py-0.5 text-xs ${
                      form.etiquetas.includes(e.id)
                        ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 text-slate-700"
                    }`}
                    data-testid={`wa-dif-etiqueta-${e.id}`}
                  >
                    {e.nombre}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {form.tipo === "lista" ? (
        <div className="space-y-2">
          <Label htmlFor="wa-dif-texto" className="text-xs text-slate-600">
            Un teléfono por línea, con código de país. Después de una coma podés poner el nombre.
          </Label>
          <Textarea
            id="wa-dif-texto"
            value={form.texto}
            onChange={(e) => poner({ texto: e.target.value })}
            placeholder="+57 300 111 0001, Ana"
            className="min-h-[120px] text-base lg:text-sm"
            data-testid="wa-dif-texto"
          />
          <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-emerald-600"
              checked={form.acepto}
              onChange={(e) => poner({ acepto: e.target.checked })}
              data-testid="wa-dif-acepto"
            />
            Confirmo que estos clientes aceptaron que les escribamos por WhatsApp.
          </label>
        </div>
      ) : null}
    </div>
  );
}

function PasoVistaPrevia({ previa, buscando, onBuscar }) {
  if (previa === null) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-slate-600">Todavía no se buscaron los destinatarios.</p>
        <Button onClick={onBuscar} disabled={buscando} data-testid="wa-dif-buscar">
          {buscando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          Buscar destinatarios
        </Button>
      </div>
    );
  }
  const motivos = Object.entries(previa.motivos || {});
  const limite = previa.limite_meta;
  return (
    <div className="space-y-3" data-testid="wa-dif-previa">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-2xl font-semibold text-emerald-800" data-testid="wa-dif-previa-destinatarios">
            {previa.destinatarios}
          </p>
          <p className="text-xs text-emerald-800">van a recibir el mensaje</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-2xl font-semibold text-slate-700" data-testid="wa-dif-previa-omitidos">
            {previa.omitidos}
          </p>
          <p className="text-xs text-slate-600">quedan afuera</p>
        </div>
      </div>
      {motivos.length > 0 ? (
        <ul className="space-y-0.5 text-sm text-slate-700" data-testid="wa-dif-previa-motivos">
          {motivos.map(([motivo, n]) => (
            <li key={motivo}>
              {n} · {motivo}
            </li>
          ))}
        </ul>
      ) : null}
      {previa.destinatarios === 0 ? (
        <Aviso tono="ambar" testid="wa-dif-sin-destinatarios">
          No hay a quién mandarle. Volvé a Destinatarios y cambiá el filtro.
        </Aviso>
      ) : null}
      {(previa.ejemplos || []).length > 0 ? (
        <div className="space-y-2" data-testid="wa-dif-ejemplos">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Así les llega</p>
          {previa.ejemplos.map((x, i) => (
            <div key={`${x.telefono}-${i}`} className="rounded-lg bg-[#efeae2] p-2">
              <p className="mb-1 text-[11px] text-slate-500">{x.nombre || x.telefono}</p>
              <div className="ml-auto max-w-[94%] whitespace-pre-wrap rounded-lg rounded-tr-none bg-[#d9fdd3] p-2 text-sm text-slate-800 shadow-sm">
                {x.texto}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {previa.aviso ? (
        <Aviso tono="ambar" testid="wa-dif-aviso">
          {previa.aviso}
        </Aviso>
      ) : null}
      <div className="space-y-0.5 text-xs text-slate-500">
        <p>Categoría de la plantilla: {categoriaNombre(previa.categoria)}.</p>
        {limite && limite.clientes_por_dia ? (
          <p>Límite de Meta para este número: {limite.clientes_por_dia} clientes distintos por día.</p>
        ) : null}
        <p>
          Máximo {previa.maximo} destinatarios por difusión. Salen hasta {previa.por_segundo} mensajes por segundo.
        </p>
        <p>{NOTA_COBRO}</p>
      </div>
    </div>
  );
}

function PasoConfirmar({ form, previa, marcas, plantilla, confirmacion, setConfirmacion }) {
  return (
    <div className="space-y-3" data-testid="wa-dif-confirmar">
      <Aviso tono="ambar">
        Vas a mandar la plantilla {plantilla} a {previa.destinatarios} clientes de {nombreCorto(form.marca, marcas)}.
        Lo que sale no se puede deshacer; lo que falte se puede pausar o cancelar.
      </Aviso>
      <p className="text-xs text-slate-500">{NOTA_COBRO}</p>
      <div>
        <Label htmlFor="wa-dif-cantidad" className="text-xs text-slate-600">
          Para confirmar, escribí la cantidad de destinatarios ({previa.destinatarios})
        </Label>
        <Input
          id="wa-dif-cantidad"
          inputMode="numeric"
          autoComplete="off"
          value={confirmacion}
          onChange={(e) => setConfirmacion(e.target.value)}
          className="mt-1 max-w-[12rem] text-base lg:text-sm"
          data-testid="wa-dif-cantidad"
        />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ el asistente
function Asistente({ marcas, inicial, onVolver, onListo }) {
  const [paso, setPaso] = useState(inicial ? 4 : 1);
  const [form, setForm] = useState(inicial ? formDe(inicial) : FORM_VACIO);
  const [difusionId, setDifusionId] = useState(inicial ? inicial.id : null);
  const [plantillas, setPlantillas] = useState(null);
  const [errorPlantillas, setErrorPlantillas] = useState("");
  const [etiquetas, setEtiquetas] = useState([]);
  const [tours, setTours] = useState([]); // Fix 155 (2026-10-22): para elegir tours en "Reservas por fecha de tour"
  const [trabajando, setTrabajando] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [confirmacion, setConfirmacion] = useState("");
  const marca = form.marca;

  useEffect(() => {
    if (marca === "") {
      setPlantillas([]);
      return undefined;
    }
    let vivo = true;
    setPlantillas(null);
    setErrorPlantillas("");
    api
      .get("/whatsapp/plantillas", { params: { marca_slug: marca, estado: "APPROVED" } })
      .then(({ data }) => {
        if (vivo) setPlantillas((data && data.plantillas) || []);
      })
      .catch((e) => {
        if (vivo) {
          setPlantillas([]);
          setErrorPlantillas(formatApiError(e.response?.data?.detail));
        }
      });
    return () => {
      vivo = false;
    };
  }, [marca]);

  useEffect(() => {
    let vivo = true;
    api
      .get("/whatsapp/etiquetas")
      .then(({ data }) => {
        if (vivo) setEtiquetas(Array.isArray(data) ? data : (data && data.etiquetas) || []);
      })
      .catch(() => {
        if (vivo) setEtiquetas([]);
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Fix 155 (2026-10-22): los tours activos para elegir en "Reservas por fecha de tour" (contrato 151: tour_ids).
  useEffect(() => {
    let vivo = true;
    api
      .get("/tours", { params: { activo: true } })
      .then(({ data }) => {
        if (vivo) setTours(Array.isArray(data) ? data : (data && data.items) || []);
      })
      .catch(() => {
        if (vivo) setTours([]);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const poner = (parte) => {
    if (parte.marca !== undefined && parte.marca !== form.marca) setDifusionId(null);
    setForm((f) => ({ ...f, ...parte }));
  };
  const elegida = (plantillas || []).find((p) => p.id === form.plantillaId) || null;
  const campos = elegida ? elegida.campos || [] : [];
  const nombrePlantilla = elegida ? elegida.nombre : (inicial && inicial.plantilla_nombre) || "";

  const problema = () => {
    if (paso === 1) {
      if (form.nombre.trim() === "") return "Escribí un nombre para la difusión.";
      if (form.marca === "") return "Elegí la marca.";
    }
    if (paso === 2) {
      if (elegida === null) return "Elegí una plantilla.";
      const faltan = campos.filter((c) => (c.autocompletar ? false : valorDe(form.valores, c).trim() === ""));
      if (faltan.length > 0) return "Completá la variable: " + faltan.map((c) => c.etiqueta).join(", ") + ".";
    }
    if (paso === 3) {
      if (form.tipo === "reservas" && form.desde === "" && form.hasta === "") {
        return "Elegí desde y hasta qué fecha de tour son las reservas.";
      }
      if (form.tipo === "reservas" && form.estadosReserva.length === 0) return "Elegí al menos un estado de reserva.";
      if (form.tipo === "lista" && form.texto.trim() === "") return "Pegá los teléfonos, uno por línea.";
      if (form.tipo === "lista" && form.acepto === false) {
        return "Confirmá que los clientes aceptaron que les escribamos por WhatsApp.";
      }
    }
    return "";
  };

  // Guarda lo armado (si hace falta) y busca a los destinatarios: todavia no se manda nada.
  const buscar = async (guardar) => {
    setTrabajando(true);
    try {
      let id = difusionId;
      if (guardar) {
        const cuerpo = {
          nombre: form.nombre.trim(),
          plantilla_id: form.plantillaId,
          valores: valoresLimpios(form.valores, campos),
          origen: origenDe(form),
        };
        if (id) {
          await api.put(`${BASE}/${encodeURIComponent(id)}`, cuerpo);
        } else {
          const { data } = await api.post(BASE, { ...cuerpo, marca_slug: form.marca });
          id = data.id;
          setDifusionId(id);
        }
      }
      const { data } = await api.post(`${BASE}/${encodeURIComponent(id)}/vista-previa`);
      setPrevia(data);
      setConfirmacion("");
      setPaso(4);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTrabajando(false);
    }
  };

  const siguiente = () => {
    const p = problema();
    if (p) {
      toast.error(p);
      return;
    }
    if (paso === 3) {
      buscar(true);
      return;
    }
    setPaso(paso + 1);
  };

  const enviar = async () => {
    setTrabajando(true);
    try {
      await api.post(`${BASE}/${encodeURIComponent(difusionId)}/enviar`, { confirmacion: Number(confirmacion.trim()) });
      toast.success("La difusión está saliendo.");
      onListo(difusionId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTrabajando(false);
    }
  };

  const sinDestinatarios = paso === 4 && (previa === null || previa.destinatarios === 0);
  const confirmado = previa !== null && confirmacion.trim() === String(previa.destinatarios);

  return (
    <div className="space-y-4" data-testid="wa-dif-asistente">
      <p className="text-xs font-medium text-slate-500" data-testid="wa-dif-paso">
        Paso {paso} de {PASOS.length} · {PASOS[paso - 1]}
      </p>
      {paso === 1 ? <PasoMarca form={form} poner={poner} marcas={marcas} /> : null}
      {paso === 2 ? (
        <PasoPlantilla
          form={form}
          poner={poner}
          plantillas={plantillas}
          errorPlantillas={errorPlantillas}
          elegida={elegida}
          campos={campos}
        />
      ) : null}
      {paso === 3 ? <PasoDestinatarios form={form} poner={poner} etiquetas={etiquetas} tours={tours} /> : null}
      {paso === 4 ? <PasoVistaPrevia previa={previa} buscando={trabajando} onBuscar={() => buscar(false)} /> : null}
      {paso === 5 && previa !== null ? (
        <PasoConfirmar
          form={form}
          previa={previa}
          marcas={marcas}
          plantilla={nombrePlantilla}
          confirmacion={confirmacion}
          setConfirmacion={setConfirmacion}
        />
      ) : null}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          variant="outline"
          onClick={paso === 1 ? onVolver : () => setPaso(paso - 1)}
          disabled={trabajando}
          className="gap-1.5"
          data-testid="wa-dif-atras"
        >
          <ArrowLeft className="h-4 w-4" />
          {paso === 1 ? "Cancelar" : "Atrás"}
        </Button>
        {paso === 5 ? (
          <Button onClick={enviar} disabled={trabajando || confirmado === false} data-testid="wa-dif-enviar">
            {trabajando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Mandar difusión
          </Button>
        ) : (
          <Button onClick={siguiente} disabled={trabajando || sinDestinatarios} data-testid="wa-dif-siguiente">
            {trabajando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {paso === 3 ? "Ver vista previa" : "Siguiente"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ el seguimiento
function Detalle({ difusionId, marcas, onVolver, onRevisar }) {
  const [d, setD] = useState(null);
  const [envios, setEnvios] = useState(null);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [ocupado, setOcupado] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const base = `${BASE}/${encodeURIComponent(difusionId)}`;

  const cargar = useCallback(async () => {
    try {
      const [uno, dos] = await Promise.all([
        api.get(base),
        api.get(`${base}/envios`, { params: { estado: filtro || undefined, pagina } }),
      ]);
      setD(uno.data);
      setEnvios(dos.data);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [base, filtro, pagina]);

  useEffect(() => {
    cargar();
    const reloj = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(reloj);
  }, [cargar]);

  const accion = async (nombre, ok) => {
    setOcupado(true);
    try {
      await api.post(`${base}/${nombre}`);
      toast.success(ok);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
      setCancelando(false);
    }
  };

  const bajarCsv = async () => {
    try {
      const { data } = await api.get(`${base}/envios.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = `difusion-${String(difusionId).slice(0, 8)}.csv`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (d === null) {
    return error ? (
      <div className="space-y-3">
        <Aviso tono="rojo" testid="wa-dif-error">
          {error}
        </Aviso>
        <Button variant="outline" onClick={onVolver}>
          Volver
        </Button>
      </div>
    ) : (
      <Leyendo texto="Leyendo la difusión…" />
    );
  }

  const c = d.conteo || {};
  const total = d.destinatarios || 0;
  const hechos = Math.max(total - (c.pendientes || 0) - (c.cancelados || 0), 0);
  const porcentaje = total > 0 ? Math.round((100 * hechos) / total) : 0;
  const sinSalir = d.estado === "borrador" || d.estado === "lista";
  const paginas = envios ? envios.paginas : 1;

  return (
    <div className="space-y-4" data-testid="wa-dif-detalle">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onVolver} className="gap-1.5" data-testid="wa-dif-volver">
          <ArrowLeft className="h-4 w-4" />
          Difusiones
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800">{d.nombre}</p>
          <p className="truncate text-[11px] text-slate-500">
            {nombreCorto(d.marca_slug, marcas)} · {d.plantilla_nombre} · {categoriaNombre(d.categoria)}
          </p>
        </div>
        <EstadoChip estado={d.estado} />
      </div>
      {error ? (
        <Aviso tono="rojo" testid="wa-dif-error">
          {error}
        </Aviso>
      ) : null}
      {d.estado === "pausada" && d.motivo_pausa ? (
        <Aviso tono="ambar" testid="wa-dif-motivo-pausa">
          {d.motivo_pausa}
        </Aviso>
      ) : null}
      {sinSalir ? (
        <Aviso tono="gris" testid="wa-dif-sin-salir">
          Todavía no salió ningún mensaje.
        </Aviso>
      ) : (
        <div className="space-y-1" data-testid="wa-dif-progreso">
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${porcentaje}%` }} />
          </div>
          <p className="text-xs text-slate-500">
            {hechos} de {total} procesados ({porcentaje}%)
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {CONTADORES.map(([clave, nombre]) => (
          <div key={clave} className="rounded-md border border-slate-200 px-2.5 py-1.5" data-testid={`wa-dif-cuenta-${clave}`}>
            <p className="text-lg font-semibold text-slate-800">{c[clave] || 0}</p>
            <p className="text-[11px] text-slate-500">{nombre}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {sinSalir ? (
          <Button size="sm" onClick={() => onRevisar(d)} data-testid="wa-dif-revisar">
            Revisar y mandar
          </Button>
        ) : null}
        {d.estado === "enviando" ? (
          <Button size="sm" variant="outline" disabled={ocupado} onClick={() => accion("pausar", "Difusión en pausa.")} className="gap-1.5" data-testid="wa-dif-pausar">
            <Pause className="h-4 w-4" />
            Pausar
          </Button>
        ) : null}
        {d.estado === "pausada" ? (
          <Button size="sm" disabled={ocupado} onClick={() => accion("reanudar", "La difusión sigue.")} className="gap-1.5" data-testid="wa-dif-reanudar">
            <Play className="h-4 w-4" />
            Seguir
          </Button>
        ) : null}
        {d.estado === "enviando" || d.estado === "pausada" ? (
          <Button size="sm" variant="outline" disabled={ocupado} onClick={() => setCancelando(true)} className="text-red-700 hover:text-red-800" data-testid="wa-dif-cancelar">
            Cancelar lo que falta
          </Button>
        ) : null}
        <Button size="sm" variant="outline" onClick={bajarCsv} className="gap-1.5" data-testid="wa-dif-csv">
          <Download className="h-4 w-4" />
          Descargar CSV
        </Button>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="wa-dif-filtro" className="text-xs text-slate-600">
            Estado
          </Label>
          <select
            id="wa-dif-filtro"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPagina(1);
            }}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 lg:text-sm"
            data-testid="wa-dif-filtro"
          >
            <option value="">Todos</option>
            {Object.entries(ESTADOS_ENVIO).map(([valor, nombre]) => (
              <option key={valor} value={valor}>
                {nombre}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500">{envios ? envios.total : 0} en total</span>
        </div>
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-left text-sm" data-testid="wa-dif-envios">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5">Cliente</th>
                <th className="px-2 py-1.5">Estado</th>
                <th className="px-2 py-1.5">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {((envios && envios.envios) || []).map((e) => (
                <tr key={e.id}>
                  <td className="px-2 py-1.5">
                    <span className="block text-slate-800">{e.nombre || "Sin nombre"}</span>
                    <span className="block text-[11px] text-slate-500">{e.telefono || "Sin teléfono"}</span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{ESTADOS_ENVIO[e.estado] || e.estado}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-600">{e.error || ""}</td>
                </tr>
              ))}
              {envios && envios.envios.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-2 py-4 text-center text-xs text-slate-500">
                    No hay clientes con este estado.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} data-testid="wa-dif-anterior">
            Anterior
          </Button>
          <span>
            Página {pagina} de {paginas}
          </span>
          <Button size="sm" variant="outline" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)} data-testid="wa-dif-siguiente-pagina">
            Siguiente
          </Button>
        </div>
      </div>
      <AlertDialog open={cancelando} onOpenChange={(abierto) => (ocupado ? null : setCancelando(abierto))}>
        <AlertDialogContent data-testid="wa-dif-cancelar-dialogo">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar lo que falta?</AlertDialogTitle>
            <AlertDialogDescription>
              Los mensajes que ya salieron no se pueden deshacer. Lo que todavía no salió no se va a mandar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={ocupado}>Seguir mandando</AlertDialogCancel>
            <AlertDialogAction
              disabled={ocupado}
              onClick={(e) => {
                e.preventDefault();
                accion("cancelar", "Difusión cancelada.");
              }}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="wa-dif-cancelar-confirmar"
            >
              Cancelar la difusión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ el dialogo
export default function DifusionesDialog({ open, onOpenChange, marcas }) {
  const [vista, setVista] = useState("lista");
  const [id, setId] = useState(null);
  const [inicial, setInicial] = useState(null);

  useEffect(() => {
    if (open) {
      setVista("lista");
      setId(null);
      setInicial(null);
    }
  }, [open]);

  const titulo = vista === "nueva" ? "Nueva difusión" : vista === "detalle" ? "Difusión" : "Difusiones";
  const alLista = () => {
    setVista("lista");
    setId(null);
    setInicial(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="wa-dif-dialogo">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>Una plantilla aprobada por Meta, mandada a muchos clientes de una marca. {NOTA_COBRO}</DialogDescription>
        </DialogHeader>
        {vista === "lista" ? (
          <Lista
            marcas={marcas}
            onNueva={() => {
              setInicial(null);
              setVista("nueva");
            }}
            onAbrir={(difusionId) => {
              setId(difusionId);
              setVista("detalle");
            }}
          />
        ) : null}
        {vista === "nueva" ? (
          <Asistente
            key={inicial ? inicial.id : "nueva"}
            marcas={marcas}
            inicial={inicial}
            onVolver={alLista}
            onListo={(difusionId) => {
              setInicial(null);
              setId(difusionId);
              setVista("detalle");
            }}
          />
        ) : null}
        {vista === "detalle" && id ? (
          <Detalle
            difusionId={id}
            marcas={marcas}
            onVolver={alLista}
            onRevisar={(d) => {
              setInicial(d);
              setVista("nueva");
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
